/*
 * ACS header CTA reveal
 * ---------------------------------------------------------------------------
 * The header's call to action duplicates the one in the video hero, so showing
 * both above the fold is noise. This collapses the header copy while the hero
 * is on screen and reveals it once the hero has mostly scrolled away — the
 * primary action is always one tap from anywhere below the fold, and never
 * doubled up above it.
 *
 * Whether to do this at all is decided by MEASURING, not by checking the
 * template: if no hero is present the button simply stays visible. That way a
 * hero added to (or removed from) any page needs no code change here.
 *
 * The markup renders REVEALED and this script collapses it, so a visitor with
 * no JavaScript keeps a working button rather than an invisible one.
 *
 * Collapse is grid-template-columns 1fr -> 0fr against an inner
 * overflow: hidden — the button shrinks to nothing at its own content width,
 * with no magic numbers. The stylesheet owns the transition and the
 * reduced-motion opt-out.
 */
(function () {
  if (typeof window.customElements === 'undefined' || window.customElements.get('acs-header-cta')) return;

  // Selectors for heroes that carry their own primary CTA. Kept as a list so a
  // future hero section joins by adding one entry.
  var HERO_SELECTOR = '.acs-video-hero';

  class AcsHeaderCta extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.link = this.querySelector('a');
      this.hero = document.querySelector(HERO_SELECTOR);

      // No hero on this page: nothing to defer to, leave the button alone.
      if (!this.hero || !this.link) return;

      this.frame = 0;
      this.revealed = null; // force the first update to apply

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      // Honour the current position immediately: a reload halfway down the page
      // must not animate the button in from nothing.
      this.update(true);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    onScroll() {
      if (this.frame) return;
      var self = this;
      this.frame = requestAnimationFrame(function () {
        self.frame = 0;
        self.update(false);
      });
    }

    onResize() {
      this.onScroll();
    }

    /**
     * Reveal once the hero's bottom edge has risen past a fraction of the
     * viewport. Measuring the hero itself (rather than a bare
     * `scrollY > innerHeight * n`) keeps the trigger correct whatever the hero's
     * real height is — ours is 100svh minus the sticky chrome, and it shrinks
     * further on short viewports.
     */
    update(immediate) {
      var ratio = parseFloat(this.dataset.revealRatio);
      if (!isFinite(ratio) || ratio < 0 || ratio > 1) ratio = 0.7;

      var bottom = this.hero.getBoundingClientRect().bottom;
      var revealed = bottom <= window.innerHeight * (1 - ratio);

      if (revealed === this.revealed) return;
      this.revealed = revealed;

      // Suppress the transition for the very first application so a mid-page
      // reload lands in its settled state rather than animating on arrival.
      if (immediate) this.setAttribute('data-immediate', '');

      // Collapsing is opt-in: the stylesheet's resting state is visible, so a
      // page with no hero (we return before ever getting here) and a visitor
      // with no JavaScript both keep a working button.
      this.classList.toggle('is-collapsed', !revealed);
      // Collapsed the button is visually gone, so it must leave the tab order
      // and the accessibility tree too.
      this.link.setAttribute('tabindex', revealed ? '0' : '-1');
      if (revealed) {
        this.removeAttribute('aria-hidden');
      } else {
        this.setAttribute('aria-hidden', 'true');
      }

      if (immediate) {
        // Two frames: one for the class to apply, one to release the clamp.
        var self = this;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            self.removeAttribute('data-immediate');
          });
        });
      }
    }
  }

  window.customElements.define('acs-header-cta', AcsHeaderCta);
})();
